import { useUploadProgress } from '@/stores/uploadProgressStore'
import { useOnlineStatus } from '@/hooks/useOnlineStatus'
import { copy, statusNWaitingToUpload } from '@/copy/strings'

export function SyncStatusIndicator() {
  const { isUploading, uploadPct, queuedCount, safeToClose, finalizePhase } = useUploadProgress()
  const online = useOnlineStatus()

  // Nothing to show if no pending work and no recent upload
  if (safeToClose && finalizePhase !== 'complete') {
    return null
  }

  if (finalizePhase === 'complete' && safeToClose) {
    return (
      <div
        className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-green-900/30 text-xs text-green-400"
        role="status"
        aria-live="assertive"
      >
        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
        </svg>
        {copy.statusUploaded}
      </div>
    )
  }

  if (!online && queuedCount > 0) {
    return (
      <div
        className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-yellow-900/30 text-xs text-yellow-400"
        role="status"
        aria-live="polite"
      >
        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01" />
        </svg>
        {copy.statusWaitingForConnection}
      </div>
    )
  }

  if (isUploading) {
    return (
      <div
        className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-navy-light/50 text-xs text-neon-cyan"
        role="status"
        aria-live="polite"
      >
        <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
        </svg>
        {copy.statusUploading}{uploadPct > 0 ? ` ${uploadPct}%` : ''}
      </div>
    )
  }

  if (queuedCount > 0) {
    return (
      <div
        className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-orange/10 text-xs text-orange"
        role="status"
        aria-live="polite"
      >
        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
        </svg>
        {statusNWaitingToUpload(queuedCount)}
      </div>
    )
  }

  return null
}
